import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { policiesKeys } from "../queryKeys/queryKeys";
import { policiesApi } from "@repo/api/clients/policies.api";
import { PermissionType } from "@repo/api/schemas/policies.schema";

/** Returned when the permissions API is disabled by the host. */
const DEFAULT_ENABLED_PERMISSION: PermissionType = { enabled: true };

// Known permission keys in one place
export const PERMISSION_KEYS = {
  ISPM_POLICY_MANAGE: "ispm:policy:manage",
  ISPM_FINDINGS_MANAGE: "ispm:finding:manage",
} as const;

// Type for known permissions, allows autocomplete, but won't throw on new ones
export type KnownPermissionKey =
  (typeof PERMISSION_KEYS)[keyof typeof PERMISSION_KEYS];

const useGetUserPermissions = (
  permissionKey: KnownPermissionKey,
  skipPermissions: boolean = true,
) => {
  const {
    data: permissionsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: policiesKeys.permissions(),
    queryFn: () => policiesApi.getUserPermissions(),
    // When the host disables the permissions API, skip the network call
    // entirely. All callers receive the default enabled permission below.
    enabled: !skipPermissions,
  });

  const permission = useMemo<PermissionType | undefined>(() => {
    if (skipPermissions) {
      // Return a stable default so every permission check passes without
      // a network round-trip. No licenseDisplayMetadata — callers that
      // read it will get undefined, which is the safe fallback.
      return DEFAULT_ENABLED_PERMISSION;
    }

    return permissionsData?.data?.permissions?.[permissionKey];
  }, [skipPermissions, permissionsData, permissionKey]);

  return {
    permission,
    isLoading: skipPermissions ? false : isLoading,
    isError: skipPermissions ? false : isError,
  };
};

export default useGetUserPermissions;
